import React, { useEffect, useRef, useState, useMemo } from "react";
import moment from "moment";
import axios from "axios";
import { toast } from "react-toastify";
import { FaChevronDown, FaHandPointDown } from "react-icons/fa";
import { useNavigate } from "react-router";
import { useMediaQuery } from "react-responsive";

import { StorageContainer } from "./component/styled/selfService";
import { roundMinutes } from "../mypage/kiosk/component/function/kiosk";
import { useDisableScroll } from "../../hooks/useDisableScroll";

import Header from "./component/common/header";
import TelFields from "./component/common/telFields";
import StorageMode from "./component/storage/storageMode";
import StorageType from "./component/storage/storageType";
import PinFields from "./component/common/pinFields";
import PinConfirmFields from "./component/common/pinConfirmFields";
import ActionConfirmation from "./component/common/actionConfirmation";

import { ActionConfirmationContainer } from "./component/styled/common";
import { LuSnowflake, LuPackage, LuBriefcase } from "react-icons/lu";
import { requestCardApprove } from "../../utils/nvcat";

import KioskIdleModal from "./component/common/KioskIdleModal";
import LockerOpenResultModal from "./component/common/LockerOpenResultModal";
import { useKioskProtection } from "./hooks/useKioskProtection";
import { useKioskIdleTimer } from "./hooks/useKioskIdleTimer";

import { requestOpenLockCommands } from "../../utils/lockerCommand";

const STORAGE_LABELS = { 0: "냉장", 1: "상온", 2: "캐리어" };
const STORAGE_ICONS = {
  0: LuSnowflake,
  1: LuPackage,
  2: LuBriefcase,
};
const NORMAL_STORAGE_PRICES = { 0: 3000, 1: 2000, 2: 3000 };
const PICKUP_STORAGE_PRICES = { 0: 10000, 1: 9000, 2: 10000 };

const PaymentSummary = ({
  transformValue,
  chosenStorage,
  prices,
  onPay,
  isPaying,
  modeLabel,
}) => {
  const labels = STORAGE_LABELS;
  const icons = STORAGE_ICONS;

  const items = useMemo(() => {
    return Object.entries(chosenStorage)
      .map(([type, qty]) => ({ type: Number(type), qty: Number(qty) }))
      .filter((x) => x.qty > 0);
  }, [chosenStorage]);

  const totalPrice = useMemo(() => {
    return items.reduce((sum, it) => {
      const unit = prices?.[it.type] ?? 0;
      return sum + unit * it.qty;
    }, 0);
  }, [items, prices]);

  return (
    <ActionConfirmationContainer
      className="slideContent paymentSummary"
      style={{ transform: `translateX(${transformValue}%)` }}
    >
      <div className="payWrap">
        <div className="payHeader">
          <div className="badge">STEP 6</div>
          <div className="title">결제</div>
          <div className="sub">
            선택한 보관 수량과 금액을 확인한 뒤 <b>결제하기</b>를 눌러주세요.
          </div>
        </div>

        <div className="payCard">
          <div className="payCardTop">
            <div className="left">
              <div className="cardTitle">보관 내역</div>
              <div className="cardDesc">{modeLabel} / 냉장 / 상온 / 캐리어</div>
            </div>
            <div className="right">
              <div className="pill">총 {items.reduce((a, b) => a + b.qty, 0)}개</div>
            </div>
          </div>

          <div className="payList">
            {items.map((it) => {
              const unit = prices?.[it.type] ?? 0;
              const line = unit * it.qty;
              const Icon = icons[it.type] ?? LuPackage;

              return (
                <div className="payRow" key={it.type}>
                  <div className="rowLeft">
                    <div className={`icon iconType${it.type}`}>
                      <Icon />
                    </div>
                    <div className="meta">
                      <div className="name">{labels[it.type] ?? `타입 ${it.type}`}</div>
                      <div className="calc">
                        {it.qty}개 × {unit.toLocaleString()}원
                      </div>
                    </div>
                  </div>
                  <div className="rowRight">{line.toLocaleString()}원</div>
                </div>
              );
            })}
          </div>

          <div className="divider" />

          <div className="totalBox">
            <div className="label">총 금액</div>
            <div className="value">{totalPrice.toLocaleString()}원</div>
          </div>

          <div className="payActions">
            <button className="payBtn" onClick={onPay} disabled={isPaying || totalPrice <= 0}>
              {isPaying ? "결제 진행중..." : "결제하기"}
            </button>

            <div className="hint">결제 완료 후 예약이 확정됩니다.</div>
          </div>
        </div>
      </div>
    </ActionConfirmationContainer>
  );
};

const extractCompletedStorages = (data) => {
  if (!Array.isArray(data)) return [];

  return data
    .filter((item) => item && item.storageId !== null && item.storageId !== undefined)
    .map((item, index) => ({
      id: item.id ?? index,
      reserveId: item.reserveId,
      storageId: item.storageId,
      type: Number(item.type),
      label: STORAGE_LABELS[Number(item.type)] ?? "보관함",
    }));
};

const Storage = () => {
  useDisableScroll();
  useKioskProtection({ enabled: true });

  const navigate = useNavigate();
  const isMobile = useMediaQuery({ query: "(max-width:767px)" });

  /**
   * step
   * 0 보관 방식 선택
   * 1 보관 유형 선택
   * 2 전화번호
   * 3 PIN
   * 4 PIN 확인
   * 5 결제 확인
   * 6 완료
   */
  const [active, setActive] = useState(0);
  const transformValue = active * -100;

  const [formData, setFormData] = useState({
    mberNm: "",
    tel: "010",
    password: "",
    passwordConfirm: "",
    pickupProduct: null,
  });

  const [chosenStorage, setChosenStorage] = useState({ 0: 0, 1: 0, 2: 0 });
  const total = Object.values(chosenStorage).reduce((a, b) => a + b, 0);

  const isDuplicateCheck = useRef(false);
  const [isPaying, setIsPaying] = useState(false);

  const storagePrices = useMemo(
    () => (formData.pickupProduct ? PICKUP_STORAGE_PRICES : NORMAL_STORAGE_PRICES),
    [formData.pickupProduct]
  );

  const inputFields = [
    { label: "보관 방식" },
    { label: "보관 유형" },
    { label: "전화번호" },
    { label: "비밀번호" },
    { label: "비밀번호 확인" },
  ];

  const [completedInfo, setCompletedInfo] = useState({
    storages: [],
    explanation: [],
  });
  const [isOpeningLockers, setIsOpeningLockers] = useState(false);
  const [openedStorages, setOpenedStorages] = useState([]);
  const [isLockerResultOpen, setIsLockerResultOpen] = useState(false);
  const showNextCue = active === 0 && formData.pickupProduct !== null;

  const resetStorageFlow = () => {
    setActive(0);
    setFormData({
      mberNm: "",
      tel: "010",
      password: "",
      passwordConfirm: "",
      pickupProduct: null,
    });
    setChosenStorage({ 0: 0, 1: 0, 2: 0 });
    setCompletedInfo({
      storages: [],
      explanation: [],
    });
    setIsOpeningLockers(false);
    setOpenedStorages([]);
    setIsLockerResultOpen(false);
    setIsPaying(false);
    isDuplicateCheck.current = false;
  };

  const { isWarningOpen, countdown, closeWarning } = useKioskIdleTimer({
    enabled: !isPaying && active !== 6,
    timeout: active === 5 ? 120000 : 90000,
    warningDuration: 15000,
    onTimeout: () => {
      resetStorageFlow();
      navigate("/self-service", { replace: true });
    },
  });

  useEffect(() => {
    if (active !== 6 || isLockerResultOpen || isOpeningLockers) {
      return;
    }

    const timer = setTimeout(() => {
      resetStorageFlow();
      navigate("/self-service", { replace: true });
    }, 15000);

    return () => clearTimeout(timer);
  }, [active, isLockerResultOpen, isOpeningLockers, navigate]);

  const handleChangeInput = (type, value) => {
    if (type === "tel") {
      let next = value.replace(/\D/g, "");

      if (!next.startsWith("010")) {
        next = `010${next.replace(/^010/, "")}`;
      }

      next = next.slice(0, 11);

      setFormData((prev) => ({
        ...prev,
        tel: next,
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,
      [type]: value,
    }));
  };

  const handleSelectStorageMode = (pickupProduct) => {
    setFormData((prev) => ({
      ...prev,
      pickupProduct,
    }));
  };

  const determineStepAction = (isNext = false) => {
    if (active === 6) return;

    if (!isNext && active < 1) return;
    if (isNext && active >= 5) return;

    if (isNext) {
      if (!stepCheck()) return;
      setActive((prev) => prev + 1);
    } else {
      setActive((prev) => prev - 1);
    }
  };

  const stepCheck = () => {
    if (active === 0 && formData.pickupProduct === null) {
      toast.error("보관 방식을 선택해주세요.");
      return false;
    }

    if (active === 1 && !total) {
      toast.error("보관 수량을 선택해주세요.");
      return false;
    }

    if (active === 2) {
      const tel = formData.tel.replace(/\D/g, "");
      const telRegex = /^010[0-9]{8}$/;

      if (!telRegex.test(tel)) {
        toast.error("유효한 전화번호를 입력해주세요.");
        return false;
      }
    }

    if (active === 3) {
      if (!/^[0-9]{4}$/.test(formData.password)) {
        toast.error("비밀번호는 숫자 4자리입니다.");
        return false;
      }
    }

    if (active === 4) {
      if (formData.password !== formData.passwordConfirm) {
        toast.error("비밀번호가 일치하지 않습니다.");
        return false;
      }
    }

    return true;
  };

  const handlePay = async () => {
    if (isPaying) return;

    const items = Object.entries(chosenStorage)
      .map(([type, qty]) => ({ type: Number(type), qty: Number(qty) }))
      .filter((x) => x.qty > 0);

    const totalPrice = items.reduce((sum, it) => {
      const unit = storagePrices?.[it.type] ?? 0;
      return sum + unit * it.qty;
    }, 0);

    if (totalPrice <= 0) {
      toast.error("결제 금액이 올바르지 않습니다.");
      return;
    }

    setIsPaying(true);

    try {
      const paymentResult = await requestCardApprove({
        amount: totalPrice,
        tax: "0",
        bongsa: "0",
        halbu: "00",
      });

      console.log("[KIOSK] paymentResult:", paymentResult);

      toast.success(
        `결제 성공! 승인번호:${paymentResult.approvalNo} / 승인일시:${paymentResult.approvedAt}`
      );

      await handleReserveCompleteForKiosk(paymentResult);
    } catch (e) {
      console.error("[NVCAT] request error:", e);
      toast.error(e?.message || "NVCAT 통신 실패 (카드기 실행 여부 확인)");
    } finally {
      setIsPaying(false);
    }
  };

  const handleReserveCompleteForKiosk = async (paymentResult) => {
    if (isDuplicateCheck.current) {
      toast.error("처리 중입니다. 잠시만 기다려 주세요.");
      return;
    }

    isDuplicateCheck.current = true;

    try {
      const body = {
        point: "bank",
        pickupProduct: Boolean(formData.pickupProduct),
        info: {
          mberNm: formData.mberNm || "",
          tel: formData.tel.replace(/\D/g, ""),
          os: "kiosk",
          payType: 1,
          pickupProduct: Boolean(formData.pickupProduct),
          reservationDay: moment().format("YYYYMMDD"),
          reservationStartTime: roundMinutes(),
          reservationTime: 180,
          orderId: "",
          amount: String(paymentResult.amount ?? 0),
          paymentKey: "",
          couponCodeId: null,
          couponPrice: 0,
        },
        storages: Object.entries(chosenStorage)
          .map(([type, qty]) => ({
            type: Number(type),
            quantity: Number(qty),
          }))
          .filter((item) => item.quantity > 0),
        payment: {
          approvalNo: paymentResult.approvalNo,
          approvedAt: paymentResult.approvedAt,
          cardName: paymentResult.cardName,
          cardNoMasked: paymentResult.cardNoMasked,
          catId: paymentResult.catId,
          amount: Number(paymentResult.amount),
          resultCode: paymentResult.resultCode,
          rawResponse: paymentResult.rawResponse,
        },
        pwd: formData.password,
        memo: formData.pickupProduct
          ? "키오스크 야구장 픽업 보관"
          : "키오스크 일반 보관",
      };

      console.log("[KIOSK] reserve-complete request body:", body);

      const res = await axios.post("/api/kiosk/reserve-complete", body);

      console.log("[KIOSK] reserve-complete response:", res.data);

      if (res.status === 200 || res.status === 201) {
        const completedStorages = extractCompletedStorages(res.data);

        const printPayload = {
          storeName: "꿈돌이 빵다방 은행점",
          tel: formData.tel,
          storages: completedStorages,
          storedAt: new Date().toISOString(),
        };

        try {
          const printRes = await fetch("http://127.0.0.1:18181/print", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(printPayload),
          });

          if (!printRes.ok) {
            const printMessage = await printRes.text();
            console.error("[PRINT] print failed:", printMessage);
          }
        } catch (printError) {
          console.error("[PRINT] print request error:", printError);
        }

        setCompletedInfo({
          storages: completedStorages,
          explanation: formData.pickupProduct
            ? [
                "야구장 픽업 보관 신청이 완료되었습니다.",
                "아래 배정된 보관함에 짐을 넣어주세요.",
                "짐을 넣은 뒤 문을 닫아주세요.",
                "경기 종료 후 경기장 앞에서 픽업할 수 있어요.",
              ]
            : [
                "보관 신청이 완료되었습니다.",
                "아래 배정된 보관함에 짐을 넣어주세요.",
                "짐을 넣은 뒤 문을 닫아주세요.",
                "찾을 때는 전화번호와 비밀번호를 입력해주세요.",
              ],
        });
        toast.success("보관 신청 및 결제가 완료되었습니다.");
        setActive(6);

        setIsOpeningLockers(true);

        try {
          await requestOpenLockCommands({
            point: "bank",
            storages: completedStorages,
            pulseMs: 400,
            requestedBy: "kiosk",
            requestNote: "키오스크 보관 완료 후 문 열기",
            keyPrefix: "kiosk-store-open",
            reserveId: completedStorages?.[0]?.reserveId,
          });

          setOpenedStorages(completedStorages);
          setIsLockerResultOpen(true);
        } catch (openError) {
          console.error("[LOCK] open lock failed", openError);
          toast.error("보관함 문을 열지 못했습니다. 관리자에게 문의해주세요.");
        } finally {
          setIsOpeningLockers(false);
        }

        return;
      }

      throw new Error(`Unexpected response status: ${res.status}`);
    } catch (e) {
      console.error("[KIOSK] reserve-complete error:", e);
      toast.error(
        e?.response?.data?.message ||
          e?.response?.data ||
          e?.message ||
          "예약 완료 처리에 실패했습니다."
      );
    } finally {
      isDuplicateCheck.current = false;
    }
  };

  return (
    <StorageContainer>
      <Header title="보관 신청" handleBackButton={determineStepAction} />

      <div className="content">
        {active < 5 && (
          <div className="sidemenu">
            <ul>
              {inputFields.map((item, idx) => (
                <React.Fragment key={idx}>
                  <li className={active === idx ? "active" : ""}>{item.label}</li>
                  {idx !== inputFields.length - 1 && (
                    <li>
                      <FaChevronDown />
                    </li>
                  )}
                </React.Fragment>
              ))}
            </ul>
            <div className="nextActionArea">
              {showNextCue && (
                <div className="nextCue" role="status" aria-live="polite">
                  <strong>선택이 완료됐어요!</strong>
                  <span>
                    아래 <b>다음</b> 버튼을 눌러주세요
                  </span>
                  <FaHandPointDown className="nextCueIcon" aria-hidden="true" />
                </div>
              )}
              <button
                className={showNextCue ? "nextButtonReady" : ""}
                onClick={() => determineStepAction(true)}
              >
                다음
              </button>
            </div>
          </div>
        )}

        <div
          className="slideWrapper"
          style={{ width: active < 5 && !isMobile ? "calc(100% - 250px)" : "100%" }}
        >
          <StorageMode
            transformValue={transformValue}
            pickupProduct={formData.pickupProduct}
            onSelect={handleSelectStorageMode}
          />

          <StorageType
            transformValue={transformValue}
            chosenStorage={chosenStorage}
            setChosenStorage={setChosenStorage}
            total={total}
            pickupProduct={formData.pickupProduct}
            prices={storagePrices}
          />

          <TelFields
            transformValue={transformValue}
            formData={formData}
            handleChangeInput={handleChangeInput}
          />

          <PinFields
            transformValue={transformValue}
            formData={formData}
            handleChangeInput={handleChangeInput}
          />

          <PinConfirmFields
            transformValue={transformValue}
            formData={formData}
            handleChangeInput={handleChangeInput}
          />

          <PaymentSummary
            transformValue={transformValue}
            chosenStorage={chosenStorage}
            prices={storagePrices}
            onPay={handlePay}
            isPaying={isPaying}
            modeLabel={
              formData.pickupProduct ? "야구장 픽업 보관" : "일반 보관 직접찾기"
            }
          />

          <ActionConfirmation
            transformValue={transformValue}
            title={
              formData.pickupProduct ? "야구장 픽업 보관 신청 완료" : "보관 신청 완료"
            }
            explanation={
              completedInfo.explanation.length > 0
                ? completedInfo.explanation
                : formData.pickupProduct
                  ? [
                      "야구장 픽업 보관 신청이 완료되었습니다.",
                      "경기 종료 후 경기장 앞에서 픽업할 수 있어요.",
                    ]
                  : [
                      "보관 신청이 완료되었습니다.",
                      "찾을 때는 전화번호와 비밀번호를 입력해주세요.",
                    ]
            }
            storages={completedInfo.storages}
            tel={formData.tel}
            onConfirm={() => {
              resetStorageFlow();
              navigate("/self-service", { replace: true });
            }}
          />
        </div>
      </div>

      <KioskIdleModal
        open={isWarningOpen}
        countdown={countdown}
        onContinue={closeWarning}
        onExit={() => {
          resetStorageFlow();
          navigate("/self-service", { replace: true });
        }}
      />

      <LockerOpenResultModal
        open={isLockerResultOpen}
        storages={openedStorages}
        onConfirm={() => {
          resetStorageFlow();
          navigate("/self-service", { replace: true });
        }}
      />
    </StorageContainer>
  );
};

export default Storage;
